import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ManhwaRecap } from '../types';

interface ModalContextType {
  selectedRecap: ManhwaRecap | null;
  isModalOpen: boolean;
  openModal: (recap: ManhwaRecap) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [selectedRecap, setSelectedRecap] = useState<ManhwaRecap | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (recap: ManhwaRecap) => {
    setSelectedRecap(recap);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRecap(null);
  }, []);

  // Handle browser back button to close modal instead of navigating away
  useEffect(() => {
    // Delay popstate handling to avoid interfering with initial modal opening
    const timeoutId = setTimeout(() => {
      const currentUrl = window.location.href;

      const handlePopState = () => {
        if (isModalOpen) {
          // Close modal when back button is pressed
          closeModal();
          // Replace the current history state to prevent navigation
          window.history.replaceState(null, '', currentUrl);
        }
      };

      if (isModalOpen) {
        window.addEventListener('popstate', handlePopState);
      }

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }, 100); // Small delay to avoid interference

    return () => clearTimeout(timeoutId);
  }, [isModalOpen, closeModal]);

  return (
    <ModalContext.Provider value={{
      selectedRecap,
      isModalOpen,
      openModal,
      closeModal
    }}>
      {children}
    </ModalContext.Provider>
  );
};
